import { EDoorWindowSide } from '@/editor/data/model/model';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { getFootRatio, getLength, getPointFromRatio } from '@/editor/math/shape/other';
import { EEntityType } from '@/editor/types';
import { IShapeSeg2d } from '@/editor/types/shape';
import { getWindowRotation } from '@/editor/utils/window';
import { globalStores } from '@/editor/stores';
import { LineSeg2d } from '@/editor/math/LineSeg2d';

interface IAbsorbWallInfo {
  wallId: string;
  wallType: EEntityType.Wall | EEntityType.SeparateWall;
  seg: IShapeSeg2d;
  rawSeg?: LineSeg2d;
}

interface IAbsorbInfo {
  wallId: string;
  wallType: EEntityType.Wall | EEntityType.SeparateWall;
  absorbPoint: IPoint2d;
  seg: IShapeSeg2d;
  rotation: number;
  distance: number;
}

interface IWindowAbsorbWallStore {
  getWallInfos(): IAbsorbWallInfo[];
}

export class WindowAbsorbCalculator {
  private _wallStore: IWindowAbsorbWallStore;
  private _wallInfos: IAbsorbWallInfo[] = [];

  constructor(wallStore: IWindowAbsorbWallStore) {
    this._wallStore = wallStore;
  }

  updateWallData() {
    this._wallInfos = this._wallStore.getWallInfos();
  }

  calculate(params: { rawPoint: IPoint2d; margin: number; side: EDoorWindowSide }): IAbsorbInfo | null {
    const { rawPoint, margin, side } = params;
    const dynamicAbsorbDistance = globalStores.dynamicAbsorbDistance;

    let minDistance = Infinity;
    let absorbInfo: Omit<IAbsorbInfo, 'rotation'> | null = null;

    // 计算相邻墙线段/墙延长线
    for (const wallInfo of this._wallInfos) {
      const wallSeg = wallInfo.seg;
      const wallLength = getLength(wallSeg);
      // 墙体不够长，不吸附

      if (wallLength < margin * 2) continue;

      const footRatio = getFootRatio(wallSeg, rawPoint);

      if (footRatio < 0 || footRatio > 1) continue;

      const footPoint = getPointFromRatio(wallSeg, footRatio);

      const rawPointToFootDistance = Point2dUtils.distanceTo(footPoint, rawPoint);
      if (rawPointToFootDistance > dynamicAbsorbDistance || rawPointToFootDistance > minDistance) continue;

      minDistance = rawPointToFootDistance;
      absorbInfo = {
        wallId: wallInfo.wallId,
        wallType: wallInfo.wallType,
        absorbPoint: footPoint,
        seg: wallSeg,
        distance: wallLength * footRatio
      };
      if (wallSeg instanceof ArcSeg2d) continue;

      // 门窗还需要限制到两端点的距离，弧形墙暂不处理
      const startTofootPointDistance = Point2dUtils.distanceTo(wallSeg.start, footPoint);
      const endTofootPointDistance = Point2dUtils.distanceTo(wallSeg.end, footPoint);

      if (startTofootPointDistance < margin) {
        absorbInfo.absorbPoint = Point2dUtils.addDistance(
          wallSeg.start,
          margin,
          Point2dUtils.getDirection(wallSeg.start, wallSeg.end)
        );
        absorbInfo.distance = Point2dUtils.distanceTo(absorbInfo.absorbPoint, wallSeg.start);
      } else if (endTofootPointDistance < margin) {
        absorbInfo.absorbPoint = Point2dUtils.addDistance(
          wallSeg.end,
          -margin,
          Point2dUtils.getDirection(wallSeg.start, wallSeg.end)
        );
        absorbInfo.distance = Point2dUtils.distanceTo(absorbInfo.absorbPoint, wallSeg.start);
      }

      // 使用原始墙体数据而非轮廓拆分数据来计算distance，避免后续门窗数据同步时出现位移
      if (wallInfo.rawSeg) {
        absorbInfo.distance = Point2dUtils.distanceTo(absorbInfo.absorbPoint, wallInfo.rawSeg.start);
      }
    }

    if (!absorbInfo) return null;

    const { seg, absorbPoint } = absorbInfo;
    const rotation = getWindowRotation(seg, absorbPoint, side === EDoorWindowSide.right);
    return { ...absorbInfo, rotation };
  }
}
